import React from "react";
import "./Inicio.css";
import Wrapper from "../components/basic/wrapper";
import SafeArea from "../components/basic/safe-area";
import AboutLaPlaza from "../components/sections/obra/AboutLaPlaza";
import aboutBg from "../assets/images/webp/inicio_director.webp";
import aboutBgShadowed from "../assets/images/webp/inicio_director_shadowed.webp";
import Networks from "../components/basic/social-networks-row/Networks";
import home from "../assets/images/videos/home.mp4";
import Participants from "../components/sections/inicio/Participants";
import ParticipantsMobile from "../components/sections/inicio/ParticipantsMobile";
import PhotoLinerV2 from "../components/basic/photo-liner-v2/PhotoLinerV2";
import { useI18n } from "../i18n/i18n";

const Inicio: React.FC = () => {
  const sectionColor = "#ffffff";
  const sectionTextColor = "#FFFFFF";

  const { t } = useI18n();

  const [isFichaOpen, setIsFichaOpen] = React.useState(false);
  const toggleFicha = () => setIsFichaOpen((prev) => !prev);

  // preload both backgrounds once
  const usePreloadImages = (urls: string[]) => {
    React.useEffect(() => {
      urls.forEach((url) => {
        if (!url) return;
        const img = new Image();
        img.src = url;
      });
    }, [urls]);
  };
  usePreloadImages([aboutBg, aboutBgShadowed]);

  // lock body scroll when ficha is open
  React.useEffect(() => {
    document.body.style.overflow = isFichaOpen ? "hidden" : "";
    return () => {
      document.body.style.overflow = "";
    };
  }, [isFichaOpen]);

  const heroLines = React.useMemo(() => {
    return t("home.heroText").split("\n").filter(Boolean);
  }, [t]);

  return (
    <>
      <Wrapper
        color={sectionColor}
        footerColor={sectionTextColor}
        logoColor={sectionTextColor}
      >
        <>
          <div style={{ backgroundColor: sectionColor }}>
            <div className="inicio-photo">
              <video
                className="inicio-video"
                src={home}
                autoPlay
                loop
                muted
                playsInline
              />

              <div className="inicio-intro-text fs20 tal ln26">
                {heroLines.map((line, idx) => (
                  <div key={idx}>{line}</div>
                ))}
              </div>

              <div className="inicio-photo-redes w100">
                <SafeArea>
                  <Networks extended scrollTargetId="inicio-scroll-target" />
                </SafeArea>
              </div>
            </div>
          </div>

          <div id="inicio-scroll-target" />
          <Participants />
          <ParticipantsMobile />

          {/* Desktop director section */}
          <div
            className={`inicio-director-desktop ${isFichaOpen ? "is-open" : ""}`}
            style={{
              backgroundImage: `url(${isFichaOpen ? aboutBgShadowed : aboutBg})`,
            }}
          >
            <PhotoLinerV2
              title={t("sections.theDirector")}
              color={isFichaOpen ? "#ffffff" : "#000000"}
              customClass="fs32 fw700"
              absolute
              onHoverChange={toggleFicha}
              hovered={isFichaOpen}
            />

            <div
              className={
                "inicio-director-overlay" + (isFichaOpen ? " is-visible" : "")
              }
            >
              <div className="inicio-director-overlay-inner tal">
                <SafeArea>
                  <div className="fs16 ln26">
                    <div className="ln26 fs16 mb36">
                      <b>Chela De Ferrari</b>: {t("home.directorBio.p1")}
                    </div>

                    <div className="ln26 fs16 mb36">{t("home.directorBio.p2")}</div>
                    <div className="ln26 fs16 mb36">{t("home.directorBio.p3")}</div>
                    <div className="ln26 fs16 mb36">{t("home.directorBio.p4")}</div>
                    <div className="ln26 fs16 mb36">{t("home.directorBio.p5")}</div>
                    <div className="ln26 fs16 mb36">{t("home.directorBio.p6")}</div>
                  </div>
                </SafeArea>
              </div>
            </div>
          </div>

          {/* Mobile director section */}
          <div className={`inicio-director-mobile ${isFichaOpen ? "is-open" : ""}`}>
            <img
              className="objcov"
              src="https://images-extras.s3.us-east-1.amazonaws.com/inicio_director_mobile.png"
              alt=""
            />

            <PhotoLinerV2
              title={t("sections.theDirector")}
              color="#ffffff"
              customClass="fs32 fw700"
              hovered
              absolute
            />

            <SafeArea>
              <div className="fs16 ln26">
                <div className="ln26 fs16 mb36">
                  <b>Chela De Ferrari</b>: {t("home.directorBio.p1")}
                </div>

                <div className="ln26 fs16 mb36">{t("home.directorBio.p2")}</div>
                <div className="ln26 fs16 mb36">{t("home.directorBio.p3")}</div>
                <div className="ln26 fs16 mb36">{t("home.directorBio.p4")}</div>
                <div className="ln26 fs16 mb36">{t("home.directorBio.p5")}</div>
                <div className="ln26 fs16 mb36">{t("home.directorBio.p6")}</div>
              </div>
            </SafeArea>

            <div style={{ height: 28 }} />

            <SafeArea>
              <div className="inicio-about-mobile">
                <div className="fs16 ln26">
                  <PhotoLinerV2
                    title={t("sections.aboutLaPlaza")}
                    color="#ffffff"
                    customClass="fs32 fw700"
                    hovered
                    absolute
                    safe={false}
                  />

                  <div className="ln26 fs16 mb36 mt50">{t("home.aboutLaPlaza.p1")}</div>
                  <div className="ln26 fs16 mb36">{t("home.aboutLaPlaza.p2")}</div>
                  <div className="ln26 fs16 mb36">{t("home.aboutLaPlaza.p3")}</div>
                  <div className="ln26 fs16 mb36">{t("home.aboutLaPlaza.p4")}</div>

                  <a
                    style={{ textDecoration: "underline", color: "white" }}
                    href="https://laplaza.com.pe"
                    target="_blank"
                    rel="noreferrer"
                  >
                    laplaza.com.pe
                  </a>
                </div>
              </div>
            </SafeArea>

            <div style={{ height: 40 }} />
            <div className="d-flex aic jcc">
              <img
                width={103}
                alt="logo"
                src="https://images-extras.s3.us-east-1.amazonaws.com/logo_laplaza.png"
              />
            </div>
          </div>

          <AboutLaPlaza />
        </>
      </Wrapper>
    </>
  );
};

export default Inicio;
